import type { IntentCandidate, LocalAiRuntime, VoiceManifest } from '../types/LocalAiTypes';
import { extractDeterministicParameters } from '../normalize/TranscriptNormalizer';
import { LocalLlmParser } from './LocalLlmParser';
import { patternCandidates } from './PatternMatcher';
import { SemanticMatcher } from './SemanticMatcher';

export interface IntentEngineOptions {
  semanticInstalled: boolean;
  enhancedInstalled: boolean;
  enhancedEnabled: boolean;
}

export class IntentEngine {
  private semantic?: SemanticMatcher;
  private llm?: LocalLlmParser;

  /**
   * @param halfPrecision Whether the GPU compiles fp16 shaders — see `supportsHalfPrecision`.
   * Qwen is published as fp16-quantized weights, so without it the enhanced parser has no
   * backend to run on and says so at once rather than after loading half a gigabyte.
   */
  public constructor(
    private readonly manifest: VoiceManifest,
    private readonly runtime: LocalAiRuntime,
    private readonly halfPrecision: boolean,
  ) {}

  public async understand(
    normalized: string,
    route: string,
    options: IntentEngineOptions,
  ): Promise<IntentCandidate | undefined> {
    const patterns = patternCandidates(normalized, this.manifest.intents, route);
    const topPattern = patterns[0];
    if (topPattern && topPattern.confidence >= 0.9) return topPattern;

    let candidates = patterns;
    if (options.semanticInstalled && this.manifest.features.semanticRouterEnabled) {
      this.semantic ??= new SemanticMatcher(this.manifest, this.runtime, this.halfPrecision);
      const semantic = await this.semantic.candidates(normalized, route);
      candidates = mergeCandidates(patterns, semantic, extractDeterministicParameters(normalized));
    }
    const first = candidates[0];
    const second = candidates[1];
    if (!first) return undefined;
    const ambiguous = second !== undefined
      && first.confidence - second.confidence < this.manifest.confidence.ambiguityMargin;
    if (!ambiguous && first.confidence >= this.manifest.confidence.semanticStrong) return first;

    if (options.enhancedEnabled && options.enhancedInstalled && this.manifest.features.localLlmEnabled) {
      this.llm ??= new LocalLlmParser(this.manifest, this.runtime, this.halfPrecision);
      const parsed = await this.llm.parse(normalized, candidates);
      if (parsed) return parsed;
    }
    const serverMinimum = first.source === 'pattern' || first.source === 'context' ? 0.75 : 0.72;
    return first.confidence >= Math.max(this.manifest.confidence.semanticCandidate, serverMinimum) && !ambiguous
      ? first
      : undefined;
  }

  public dispose(): void {
    this.semantic?.dispose();
    this.llm?.dispose();
  }
}

function mergeCandidates(
  patterns: readonly IntentCandidate[],
  semantic: readonly IntentCandidate[],
  deterministic: Record<string, unknown>,
): IntentCandidate[] {
  const merged = new Map<string, IntentCandidate>();
  for (const candidate of [...patterns, ...semantic]) {
    const current = merged.get(candidate.intent.id);
    const allowed = new Set(candidate.intent.parameters.map((parameter) => parameter.name));
    const parameters = {
      ...(current?.parameters ?? {}),
      ...candidate.parameters,
      ...Object.fromEntries(Object.entries(deterministic).filter(([name]) => allowed.has(name))),
    };
    if (!current || candidate.confidence > current.confidence) merged.set(candidate.intent.id, { ...candidate, parameters });
    else merged.set(candidate.intent.id, { ...current, parameters });
  }
  return [...merged.values()].sort((left, right) => right.confidence - left.confidence);
}
